<script setup lang="ts">
import camelCase from "lodash-es/camelCase";
import type { EventDetails, ProductDetails } from "mws-ts-v2-sdk";
import { AuthenticityMethod, ShippingType } from "mws-ts-v2-sdk";

type Props = {
  product: ProductDetails;
  event: EventDetails;
  productStarted: boolean;
};
const { product } = defineProps<Props>();

const { isWonByCurrentUser, sortedBids, obfuscatedUserIdForProduct } =
  useProductBids(() => product);

const showPartner = computed(() => {
  return Boolean(product.partner) && Boolean(product.partner.name);
});

const showBeneficiary = computed(() => {
  return Boolean(product.beneficiary);
});

const showAuthenticityCheck = computed(() => {
  // the V2 enum has the wrong casing due to the snake_case --> camelCase conversion not being handled for enums
  return product.authenticityMethod === camelCase(AuthenticityMethod.Fabricks);
});

const showShippingSection = computed(
  () => product.shippingType !== ShippingType.NoShipping,
);

const hasSidebarFeatures = computed(() => {
  return (
    showBeneficiary.value ||
    showAuthenticityCheck.value ||
    showPartner.value ||
    showShippingSection.value
  );
});
</script>

<template>
  <div class="sections-container px-4 md:px-0">
    <LazyBaseFireworks
      v-if="isWonByCurrentUser"
      data-test="product-sidebar-fireworks"
    />

    <div>
      <ProductSidebarTopSection
        :product="product"
        :event="event"
        :product-started="productStarted"
        class="mb-6"
      />

      <div v-if="hasSidebarFeatures">
        <LazyProductSidebarPartner
          v-if="showPartner"
          data-test="product-sidebar-partner"
          :partner="product.partner"
        />
        <LazyProductSidebarFabricks
          v-if="showAuthenticityCheck"
          data-test="product-sidebar-fabricks"
          suffix="-grey"
        />
        <LazyProductSidebarCharity
          v-if="showBeneficiary"
          data-test="product-sidebar-charity"
          :product="product"
        />
        <LazyProductSidebarShipping
          v-if="showShippingSection"
          data-test="product-sidebar-shipping"
          :product="product"
        />
      </div>
    </div>

    <LazyProductBidList
      v-if="productStarted && sortedBids.length > 0"
      :sorted-bids="sortedBids"
      :product="product"
      :obfuscated-user-id="obfuscatedUserIdForProduct"
      class="hidden md:flex flex-col gap-4"
    />

    <EngagementTrustPilotWidget
      variant="micro-star"
      class="scale-90 hidden md:block"
    />
  </div>
</template>

<style scoped lang="postcss">
.sections-container {
  @apply flex flex-col gap-6;

  > div {
    @apply pb-6 border-b last:border-none;
  }
}
</style>
